import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, LogIn, ScanFace, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  createBlockOf,
  faceLockCostLabel,
  isUnlocked,
  storybookAvatars,
  type FaceLockAvatar,
} from '@/lib/facelock';
import type { FaceLockCrewState } from '@/components/facelock/useFaceLockCrew';
import NewFaceLockDialog from '@/components/facelock/NewFaceLockDialog';
import CreateAvatarNeedsDialog from '@/components/facelock/CreateAvatarNeedsDialog';
import { faceLockScanSlot } from '@/components/facelock/scan/FaceLockScan';

// FaceLock avatars in the kids storybook (owner, 2026-10-10: parents asked to
// use the avatars they make with FaceLock Scan, as in the music video tool).
// A grown-up of the cast (mom, dad, grandma, grandpa, aunt, uncle) is drawn
// from the parent's own avatar OR from photos; the child always from a photo
// or the look picked. The storybook artist draws from face photos alone, so
// an avatar is usable here as soon as its photos passed the check
// (photos_ready), while its files may still train. Creating one is the same
// dialog, scan and rules as everywhere else (CreateAvatarNeedsDialog says what
// is missing).

export type StorybookAvatarPerson = { key: string; name: string; relation: string };

type Props = {
  /** The grown-ups of the cast an avatar may draw. */
  people: StorybookAvatarPerson[];
  /** person key -> chosen avatar id. */
  value: Record<string, string>;
  onChange: (personKey: string, avatarId: string | null) => void;
  signedIn: boolean;
  /** Keep the form and go sign in (back to this step afterwards). */
  onSignIn: (mode: 'login' | 'signup') => void;
  crew: FaceLockCrewState;
  /** Keep the form before the flat-fee checkout leaves the page. */
  onBeforeCheckout?: () => void;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

export function StorybookAvatarPicker({ people, value, onChange, signedIn, onSignIn, crew, onBeforeCheckout }: Props) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  const [needsOpen, setNeedsOpen] = useState(false);

  if (!signedIn) {
    return (
      <section
        aria-labelledby="sb-avatars-title"
        data-testid="sb-avatars-signin"
        className="space-y-3 rounded-2xl border-2 border-primary/40 bg-primary/5 p-4 sm:p-6"
      >
        <h4 id="sb-avatars-title" className="flex items-center gap-2 text-lg font-semibold">
          <ScanFace aria-hidden="true" className="h-5 w-5 text-primary" />
          {t('storybookPage.avatars.signInTitle')}
        </h4>
        <p className="text-sm text-muted-foreground">{t('storybookPage.avatars.signInText')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="button" className="h-auto min-h-11 whitespace-normal rounded-full" onClick={() => onSignIn('login')}>
            <LogIn aria-hidden="true" />
            {t('storybookPage.avatars.signIn')}
          </Button>
          <Button type="button" variant="outline" className="h-auto min-h-11 whitespace-normal rounded-full" onClick={() => onSignIn('signup')}>
            <UserPlus aria-hidden="true" />
            {t('storybookPage.avatars.signUp')}
          </Button>
        </div>
      </section>
    );
  }

  // Avatars switched off for this account: nothing to offer, the photos stay.
  if (crew.status === 'off' || (crew.status !== 'loading' && !crew.crew)) return null;
  if (crew.status === 'loading' || !crew.crew) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        {t('storybookPage.avatars.loading')}
      </p>
    );
  }

  const data = crew.crew;
  const usable = storybookAvatars(data);
  const checking = data.avatars.filter((a) => !a.photos_ready && a.status !== 'failed' && !a.photos_deleted && a.progress !== null);
  const e = data.eligibility;
  const block = createBlockOf(e, data.avatars);
  const unlocked = isUnlocked(e);

  const avatarCard = (person: StorybookAvatarPerson, a: FaceLockAvatar) => {
    const chosen = value[person.key] === a.id;
    const takenByOther = Object.entries(value).some(([key, id]) => key !== person.key && id === a.id);
    return (
      <button
        key={a.id}
        type="button"
        role="radio"
        aria-checked={chosen}
        disabled={takenByOther}
        data-testid={`sb-avatar-${person.key}-${a.id}`}
        onClick={() => onChange(person.key, chosen ? null : a.id)}
        className={cn(
          'flex min-h-16 w-full items-center gap-3 rounded-xl border-2 bg-card p-2 pr-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          chosen ? 'border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]' : 'border-border hover:border-primary/50',
          takenByOther && 'cursor-not-allowed opacity-50',
        )}
      >
        {a.picture_url ? (
          <img src={a.picture_url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" loading="lazy" />
        ) : (
          <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-bold text-muted-foreground">
            {initials(a.name)}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{a.name}</span>
          <span className="block text-xs text-muted-foreground">
            {a.castable ? t('storybookPage.avatars.ready') : t('storybookPage.avatars.photosChecked')}
          </span>
        </span>
        {chosen && <Check aria-hidden="true" className="h-5 w-5 shrink-0 text-primary" />}
      </button>
    );
  };

  return (
    <section aria-labelledby="sb-avatars-title" data-testid="sb-avatars" className="space-y-4 rounded-2xl border-2 border-primary/40 bg-primary/5 p-4 sm:p-6">
      <div>
        <h4 id="sb-avatars-title" className="flex items-center gap-2 text-lg font-semibold">
          <ScanFace aria-hidden="true" className="h-5 w-5 text-primary" />
          {t('storybookPage.avatars.title')}
        </h4>
        <p className="mt-1 text-sm text-muted-foreground">{t('storybookPage.avatars.intro')}</p>
      </div>

      {usable.length > 0 &&
        people.map((person) => (
          <div key={person.key} role="radiogroup" aria-labelledby={`sb-avatars-for-${person.key}`} className="space-y-2">
            <p id={`sb-avatars-for-${person.key}`} className="text-sm font-semibold">
              {t('storybookPage.avatars.forPerson', { name: person.name, relation: person.relation })}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {usable.map((a) => avatarCard(person, a))}
              <button
                type="button"
                role="radio"
                aria-checked={!value[person.key]}
                data-testid={`sb-avatar-${person.key}-none`}
                onClick={() => onChange(person.key, null)}
                className={cn(
                  'flex min-h-16 w-full items-center rounded-xl border-2 bg-card px-3 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  !value[person.key] ? 'border-primary' : 'border-border hover:border-primary/50',
                )}
              >
                {t('storybookPage.avatars.usePhotos')}
              </button>
            </div>
            {value[person.key] && (
              <p className="text-xs text-muted-foreground" role="status">
                {t('storybookPage.avatars.chosen', {
                  name: person.name,
                  avatar: usable.find((a) => a.id === value[person.key])?.name || '',
                })}
              </p>
            )}
          </div>
        ))}

      {checking.map((a) => (
        <div key={a.id} data-testid={`sb-avatar-checking-${a.id}`} className="space-y-1.5 rounded-xl border border-border bg-card p-3 text-sm">
          <p className="font-semibold">
            {a.progress && a.progress > 0
              ? t('storybookPage.avatars.checking', { name: a.name, progress: Math.round(a.progress) })
              : t('storybookPage.avatars.checkingStart', { name: a.name })}
          </p>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(3, Math.min(100, a.progress ?? 0))}%` }} />
          </div>
        </div>
      ))}

      {!usable.length && !checking.length && <p className="text-sm text-muted-foreground">{t('storybookPage.avatars.none')}</p>}

      <div className="space-y-1.5">
        <Button
          type="button"
          variant={usable.length ? 'outline' : 'default'}
          data-testid="sb-avatar-create"
          className="h-auto min-h-11 w-full whitespace-normal rounded-full sm:w-auto"
          onClick={() => (block ? setNeedsOpen(true) : setCreating(true))}
        >
          <ScanFace aria-hidden="true" />
          {!unlocked
            ? t('storybookPage.avatars.create')
            : t(usable.length ? 'storybookPage.avatars.createAnotherCost' : 'storybookPage.avatars.createCost', {
                cost: faceLockCostLabel(t, e.next_cost),
              })}
        </Button>
        <p className="text-xs text-muted-foreground">{t('storybookPage.avatars.createNote')}</p>
      </div>

      <NewFaceLockDialog
        open={creating}
        onOpenChange={setCreating}
        eligibility={e}
        onCreated={() => {
          void crew.refresh();
        }}
        scanSlot={faceLockScanSlot}
      />
      <CreateAvatarNeedsDialog
        open={needsOpen}
        onOpenChange={setNeedsOpen}
        block={block}
        eligibility={e}
        onBeforeCheckout={onBeforeCheckout}
        returnTo={{ path: '/storybook#builder', label: 'storybook' }}
      />
    </section>
  );
}
